const POST_LOGOUT_REDIRECT_KEY = 'postLogoutRedirect';

const hasWindow = () => 'window' in globalThis;

export const getOrigin = (): string => {
  if (!hasWindow()) return '';
  return globalThis.location.origin ?? '';
};

export const getCurrentPath = (): string => {
  if (!hasWindow()) return '';
  const { pathname = '', search = '', hash = '' } = globalThis.location;
  return `${pathname}${search}${hash}`;
};

export const normalizePostLogoutPath = (target?: string): string => {
  const fallback = getCurrentPath();

  if (!target) return fallback;

  if (target.startsWith('/') && !target.startsWith('//')) {
    return target;
  }

  const origin = getOrigin();
  if (!origin) return fallback;

  try {
    const url = new URL(target, origin);
    if (url.origin === origin) {
      return `${url.pathname}${url.search}${url.hash}`;
    }
  } catch {
    // Ignore invalid URLs and fall back to current path.
  }

  return fallback;
};

export const setPostLogoutRedirect = (target?: string): void => {
  if (!hasWindow() || !('sessionStorage' in globalThis.window)) return;
  const path = normalizePostLogoutPath(target);

  try {
    globalThis.window.sessionStorage.setItem(POST_LOGOUT_REDIRECT_KEY, path);
  } catch {
    // Ignore storage failures (e.g., private mode).
  }
};

export const consumePostLogoutRedirect = (): string | null => {
  if (!hasWindow() || !('sessionStorage' in globalThis.window)) return null;

  let stored: string | null = null;

  try {
    stored = globalThis.window.sessionStorage.getItem(POST_LOGOUT_REDIRECT_KEY);
  } catch {
    return null;
  }

  if (!stored) return null;

  try {
    globalThis.window.sessionStorage.removeItem(POST_LOGOUT_REDIRECT_KEY);
  } catch {
    // Best effort cleanup.
  }

  return normalizePostLogoutPath(stored);
};

export const getLogoutReturnTo = (): string => getOrigin();
